import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  KeyRound,
  Loader2,
  Plus,
  ShieldOff,
  Trash2,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router';

import { MarkdownRenderer } from '@/components/markdown';
import { Modal } from '@/components/modal';
import { useNotification } from '@/components/notifications';
import { PageDescription } from '@/components/page-description';
import { PageTitle } from '@/components/page-title';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/copy-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  deleteMcpServer,
  getMcpServer,
  getMcpServerAudit,
  getMcpServerDocs,
  McpAuditEvent,
  McpServer,
  mintMcpServerKey,
  revokeMcpServerKey,
} from '@/services/mcp-servers';
import { useOrgStore } from '@/stores/org-store';
import { orgPath } from '@/utils/org-path';

const formatTs = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

export const McpServerDetailPage = () => {
  const { serverId } = useParams<{ serverId: string }>();
  const { currentOrg } = useOrgStore();
  const navigate = useNavigate();
  const notification = useNotification();
  const notificationRef = useRef(notification);
  notificationRef.current = notification;

  const [server, setServer] = useState<McpServer | null>(null);
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState<{
    markdown: string;
    snippets: Record<string, unknown>;
  } | null>(null);
  const [audit, setAudit] = useState<McpAuditEvent[]>([]);
  const [keyLabel, setKeyLabel] = useState('');
  const [mintedKey, setMintedKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDrop, setConfirmDrop] = useState(false);
  const [dropName, setDropName] = useState('');

  const orgId = currentOrg?.orgid || '';

  const load = useCallback(
    async (background = false) => {
      if (!orgId || !serverId) return;
      // Background refreshes (post mint/revoke) keep the page mounted so
      // the shown-once key panel and the active tab survive the reload.
      if (!background) setLoading(true);
      try {
        const response = await getMcpServer(orgId, serverId);
        if (response.detail || !response.data?.server) {
          notificationRef.current.error(response.detail || 'Server not found');
          return;
        }
        setServer(response.data.server);
      } finally {
        if (!background) setLoading(false);
      }
    },
    [orgId, serverId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!orgId || !serverId) return;
    void getMcpServerDocs(orgId, serverId).then((response) => {
      if (response.data) setDocs(response.data);
    });
    void getMcpServerAudit(orgId, serverId).then((response) => {
      setAudit(response.data?.events || []);
    });
  }, [orgId, serverId]);

  const mint = async () => {
    if (!serverId) return;
    setBusy(true);
    try {
      const response = await mintMcpServerKey(orgId, serverId, keyLabel);
      if (response.detail || !response.data?.api_key) {
        notification.error(response.detail || 'Key minting failed');
        return;
      }
      setMintedKey(response.data.api_key);
      setKeyLabel('');
      await load(true);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (keyId: string) => {
    if (!serverId) return;
    setBusy(true);
    try {
      const response = await revokeMcpServerKey(orgId, serverId, keyId);
      if (response.detail) {
        notification.error(response.detail);
        return;
      }
      notification.success('Key revoked — access dies within ~60s');
      await load(true);
    } finally {
      setBusy(false);
    }
  };

  const drop = async () => {
    if (!serverId || !server) return;
    setBusy(true);
    try {
      const response = await deleteMcpServer(orgId, serverId);
      if (response.detail) {
        notification.error(response.detail);
        return;
      }
      notification.success(
        `"${server.name}" dropped — all credentials revoked`,
      );
      navigate(orgPath('/settings/mcp-servers'));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !server) {
    return (
      <div className='flex items-center gap-2 py-12 text-muted-foreground'>
        <Loader2 className='size-5 animate-spin' /> Loading server…
      </div>
    );
  }

  const activeKeys = (server.keys || []).filter((key) => !key.revoked_at);
  const revokedKeys = (server.keys || []).filter((key) => key.revoked_at);

  return (
    <div className='w-full'>
      <Button
        variant='ghost'
        size='sm'
        className='mb-2'
        onClick={() => navigate(orgPath('/settings/mcp-servers'))}
      >
        <ArrowLeft className='size-4' /> MCP servers
      </Button>
      <div className='mb-6 flex flex-wrap items-start justify-between gap-3'>
        <div>
          <div className='flex items-center gap-2'>
            <PageTitle title={server.name} />
            <Badge
              variant={server.status === 'active' ? 'secondary' : 'destructive'}
            >
              {server.status}
            </Badge>
          </div>
          {server.server_type === 'generic' && (
            <p className='mt-1 text-xs font-medium text-amber-600 dark:text-amber-500'>
              Org-scoped service identity — every call runs under this
              org&apos;s service credential (org-level ACLs). Per-user scoping
              coming with ENG-5281.
            </p>
          )}
          <PageDescription>{server.description}</PageDescription>
          <div className='mt-2 flex items-center gap-1 text-xs text-muted-foreground'>
            <code className='rounded bg-muted px-1.5 py-0.5'>
              {server.endpoint}
            </code>
            <CopyButton text={server.endpoint} size='small' />
          </div>
        </div>
        {server.server_type === 'generic' ? (
          <Badge variant='outline'>auto-provisioned · cannot be dropped</Badge>
        ) : (
          <Button
            variant='outline'
            className='text-destructive hover:text-destructive'
            onClick={() => setConfirmDrop(true)}
          >
            <Trash2 className='size-4' /> Drop server
          </Button>
        )}
      </div>

      <Tabs defaultValue='tools'>
        <TabsList>
          <TabsTrigger value='tools'>
            Tools (
            {server.server_type === 'generic'
              ? 'meta'
              : server.tools?.length || 0}
            )
          </TabsTrigger>
          <TabsTrigger value='credentials'>
            Credentials ({activeKeys.length})
          </TabsTrigger>
          <TabsTrigger value='docs'>Docs</TabsTrigger>
          <TabsTrigger value='audit'>Audit ({audit.length})</TabsTrigger>
        </TabsList>

        <TabsContent value='tools' className='flex flex-col gap-2 pt-3'>
          {server.server_type === 'generic' && (
            <Card>
              <CardContent className='pt-4 text-sm text-muted-foreground'>
                The org gateway serves a fixed meta-tool surface —
                list/describe/run workflows, ask agents, query data assets,
                fetch artifacts — resolved live against the organization. See
                the Docs tab for the full tool contract and the run/poll
                example.
              </CardContent>
            </Card>
          )}
          {(server.tools || []).map((tool) => (
            <Card key={tool.tool_name}>
              <CardContent className='pt-4'>
                <div className='flex items-center gap-2'>
                  <code className='text-sm font-medium'>{tool.tool_name}</code>
                  <Badge variant='outline'>{tool.kind}</Badge>
                  {tool.version_id && (
                    <Badge variant='outline'>pinned version</Badge>
                  )}
                </div>
                <p className='mt-1 text-xs text-muted-foreground'>
                  {tool.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value='credentials' className='flex flex-col gap-3 pt-3'>
          {mintedKey && (
            <Card>
              <CardContent className='pt-4'>
                <Label>New key (shown once — store it now)</Label>
                <div className='mt-1 flex items-center gap-1'>
                  <code className='flex-1 rounded bg-muted px-2 py-1 text-xs break-all'>
                    {mintedKey}
                  </code>
                  <CopyButton text={mintedKey} size='small' />
                </div>
              </CardContent>
            </Card>
          )}
          <div className='flex items-end gap-2'>
            <div className='grid flex-1 gap-1'>
              <Label htmlFor='key-label'>New key label</Label>
              <Input
                id='key-label'
                value={keyLabel}
                onChange={(event) => setKeyLabel(event.target.value)}
                placeholder='e.g. snowflake-prod'
              />
            </div>
            <Button onClick={mint} disabled={busy}>
              {busy ? (
                <Loader2 className='size-4 animate-spin' />
              ) : (
                <Plus className='size-4' />
              )}
              Mint key
            </Button>
          </div>
          {[...activeKeys, ...revokedKeys].map((key) => (
            <Card key={key.key_id}>
              <CardContent className='flex items-center gap-3 pt-4'>
                <KeyRound className='size-4 text-muted-foreground' />
                <div className='flex-1'>
                  <p className='text-sm font-medium'>{key.label}</p>
                  <p className='text-xs text-muted-foreground'>
                    Minted {formatTs(key.created_at)} by {key.created_by || '—'}
                    {key.revoked_at && ` · revoked ${formatTs(key.revoked_at)}`}
                  </p>
                </div>
                {key.revoked_at ? (
                  <Badge variant='destructive'>revoked</Badge>
                ) : (
                  <Button
                    variant='outline'
                    size='sm'
                    className='text-destructive hover:text-destructive'
                    disabled={busy}
                    onClick={() => revoke(key.key_id)}
                  >
                    <ShieldOff className='size-4' /> Revoke
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value='docs' className='pt-3'>
          {docs ? (
            <div className='prose prose-sm dark:prose-invert max-w-none'>
              <MarkdownRenderer content={docs.markdown} />
            </div>
          ) : (
            <div className='flex items-center gap-2 py-6 text-muted-foreground'>
              <Loader2 className='size-4 animate-spin' /> Generating docs…
            </div>
          )}
        </TabsContent>

        <TabsContent value='audit' className='pt-3'>
          {audit.length === 0 ? (
            <p className='py-6 text-sm text-muted-foreground'>
              No calls recorded yet. Every tools/call against this server lands
              here (tool, caller key, outcome, duration).
            </p>
          ) : (
            <div className='overflow-x-auto rounded-md border'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b bg-muted/50 text-left'>
                    <th className='px-3 py-2 font-medium'>Time</th>
                    <th className='px-3 py-2 font-medium'>Tool</th>
                    <th className='px-3 py-2 font-medium'>Caller</th>
                    <th className='px-3 py-2 font-medium'>Outcome</th>
                    <th className='px-3 py-2 font-medium'>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((event) => (
                    <tr key={event.event_id} className='border-b'>
                      <td className='px-3 py-2 whitespace-nowrap'>
                        {formatTs(event.ts)}
                      </td>
                      <td className='px-3 py-2'>
                        <code className='text-xs'>{event.tool}</code>
                      </td>
                      <td className='px-3 py-2 text-xs'>
                        {event.caller || '—'}
                      </td>
                      <td className='px-3 py-2'>
                        <Badge
                          variant={
                            event.outcome === 'success'
                              ? 'secondary'
                              : 'destructive'
                          }
                        >
                          {event.outcome}
                        </Badge>
                      </td>
                      <td className='px-3 py-2 text-xs'>
                        {event.duration_ms != null
                          ? `${event.duration_ms} ms`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Modal
        isDialogOpen={confirmDrop}
        setIsDialogOpen={setConfirmDrop}
        title={`Drop "${server.name}"`}
        description='Dropping revokes every API key and agent credential and removes the endpoint for all external consumers. Type the server name to confirm.'
        footer={
          <>
            <Button variant='outline' onClick={() => setConfirmDrop(false)}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              disabled={busy || dropName !== server.name}
              onClick={drop}
            >
              {busy ? (
                <Loader2 className='size-4 animate-spin' />
              ) : (
                <Trash2 className='size-4' />
              )}
              Drop server
            </Button>
          </>
        }
      >
        <div className='grid gap-1 py-2'>
          <Label htmlFor='drop-confirm'>Server name</Label>
          <Input
            id='drop-confirm'
            value={dropName}
            onChange={(event) => setDropName(event.target.value)}
            placeholder={server.name}
          />
        </div>
      </Modal>
    </div>
  );
};
